import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ParcelData {
  ogc_fid: number;
  address: string | null;
  city: string | null;
  county: string | null;
  
  // Owner Info
  owner: string | null;
  owner2: string | null;
  owner3: string | null;
  owner4: string | null;
  ownfrst: string | null;
  ownlast: string | null;
  owntype: string | null;
  unmodified_owner: string | null;
  previous_owner: string | null;
  careof: string | null;
  
  // Building Details
  yearbuilt: number | null;
  year_built_effective_date: number | null;
  sqft: number | null;
  area_building: number | null;
  ll_bldg_footprint_sqft: number | null;
  num_bedrooms: number | null;
  num_bath: number | null;
  num_bath_partial: number | null;
  numrooms: number | null;
  numstories: number | null;
  numunits: number | null;
  structstyle: string | null;
  
  // Lot Info
  lot: string | null;
  block: string | null;
  subdivision: string | null;
  ll_gissqft: number | null;
  gisacre: number | null;
  
  // Zoning
  zoning: string | null;
  zoning_type: string | null;
  zoning_subtype: string | null;
  zoning_description: string | null;
  zoning_code_link: string | null;
  usecode: string | null;
  usedesc: string | null;
  
  // Financial
  parval: number | null;
  landval: number | null;
  improvval: number | null;
  taxamt: number | null;
  taxyear: string | null;
  tax_rate_area: string | null;
  
  // Neighborhood
  neighborhood: string | null;
  
  // Sales History
  saleprice: number | null;
  saledate: string | null;
  last_ownership_transfer_date: string | null;
  
  // Risk & Demographics
  fema_flood_zone: string | null;
  fema_nri_risk_rating: string | null;
  median_household_income: number | null;
}

const SELECT_FIELDS = `
  ogc_fid,
  address,
  city,
  county,
  owner,
  owner2,
  owner3,
  owner4,
  ownfrst,
  ownlast,
  owntype,
  unmodified_owner,
  previous_owner,
  careof,
  yearbuilt,
  year_built_effective_date,
  sqft,
  area_building,
  ll_bldg_footprint_sqft,
  num_bedrooms,
  num_bath,
  num_bath_partial,
  numrooms,
  numstories,
  numunits,
  structstyle,
  lot,
  block,
  subdivision,
  ll_gissqft,
  gisacre,
  zoning,
  zoning_type,
  zoning_subtype,
  zoning_description,
  zoning_code_link,
  usecode,
  usedesc,
  parval,
  landval,
  improvval,
  taxamt,
  taxyear,
  tax_rate_area,
  neighborhood,
  saleprice,
  saledate,
  last_ownership_transfer_date,
  fema_flood_zone,
  fema_nri_risk_rating,
  median_household_income
`;

export function useParcelLookup() {
  const [parcelData, setParcelData] = useState<ParcelData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookupByAddress = async (searchAddress: string) => {
    setIsLoading(true);
    setError(null);
    setParcelData(null);

    try {
      const addressParts = searchAddress.split(",");
      const streetAddressRaw = addressParts[0]?.trim();
      const cityRaw = addressParts[1]?.trim();

      if (!streetAddressRaw) {
        throw new Error("Invalid address format");
      }

      const normalize = (s: string) =>
        s
          .toLowerCase()
          .replace(/[#.,]/g, " ")
          .replace(/\s+/g, " ")
          .trim();

      const STREET_SUFFIXES = new Set([
        "st", "street", "ave", "avenue", "rd", "road", "blvd", "boulevard",
        "dr", "drive", "ln", "lane", "ct", "court", "pl", "place", "way",
        "ter", "terrace", "cir", "circle", "hwy", "highway", "pkwy", "parkway",
      ]);

      const UNIT_MARKERS = new Set(["apt", "unit", "suite", "ste"]);
      const DIRECTIONS = new Set(["n", "s", "e", "w", "ne", "nw", "se", "sw"]);

      const streetTokens = normalize(streetAddressRaw).split(" ").filter(Boolean);
      const houseNumber = streetTokens[0]?.match(/^\d+[a-z]?$/i) ? streetTokens[0] : null;

      let nameTokens = houseNumber ? streetTokens.slice(1) : streetTokens;

      const unitIdx = nameTokens.findIndex((t) => UNIT_MARKERS.has(t));
      if (unitIdx >= 0) nameTokens = nameTokens.slice(0, unitIdx);

      if (nameTokens[0] && DIRECTIONS.has(nameTokens[0])) {
        nameTokens = nameTokens.slice(1);
      }

      const last = nameTokens[nameTokens.length - 1];
      if (last && STREET_SUFFIXES.has(last)) {
        nameTokens = nameTokens.slice(0, -1);
      }

      const streetNamePattern = nameTokens.length ? `%${nameTokens.join("%")}%` : "%";
      const city = cityRaw ? normalize(cityRaw) : null;

      // 1) Primary: match address components
      let { data, error: queryError } = await supabase
        .from("ca_santa_clara")
        .select(SELECT_FIELDS)
        .eq("saddno", houseNumber ?? "")
        .ilike("saddstr", streetNamePattern)
        .ilike("scity", city ? `%${city}%` : "%")
        .limit(1)
        .maybeSingle();

      // 2) Fallback: try fuzzy match on combined address text
      if (!data && !queryError) {
        const combinedPattern = houseNumber ? `%${houseNumber}%${nameTokens.join("%")} %` : `%${nameTokens.join("%")}%`;
        const combinedStreetPattern = combinedPattern.replace(/\s+/g, "%");

        const res = await supabase
          .from("ca_santa_clara")
          .select(SELECT_FIELDS)
          .ilike("address", combinedStreetPattern)
          .limit(1)
          .maybeSingle();

        data = res.data;
        queryError = res.error;
      }

      if (queryError) {
        throw queryError;
      }

      if (data) {
        setParcelData(data);
      } else {
        setError(
          "No parcel data found for this address in our Santa Clara County dataset. Try removing unit numbers (Apt/Unit) and searching again."
        );
      }
    } catch (err) {
      console.error("Parcel lookup error:", err);
      setError("Failed to look up parcel data. Please try again.");
      toast.error("Could not find parcel data for this address.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearParcelData = () => {
    setParcelData(null);
    setError(null);
  };

  return {
    parcelData,
    isLoading,
    error,
    lookupByAddress,
    clearParcelData,
  };
}
